import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Zap, Building2, MapPin, Wallet, Check, ArrowRight, ArrowLeft } from "lucide-react";

const PARISHES = ["Kingston", "St. Andrew", "St. Catherine", "Clarendon", "Manchester", "St. Elizabeth",
  "Westmoreland", "Hanover", "St. James", "Trelawny", "St. Ann", "St. Mary", "Portland", "St. Thomas"];
const TYPES = ["Hardware Store", "Retail Shop", "Wholesale", "Auto Parts", "Pharmacy", "Restaurant", "Services", "Other"];
const FASHION_TYPES = ["Fashion & Beauty", "Clothing Boutique", "Shoe Store", "Hair & Beauty Supply", "Beauty Products", "General Retail", "Other"];

const STEPS = [
  { icon: Building2, title: "Business Identity" },
  { icon: MapPin, title: "Location & Registration" },
  { icon: Wallet, title: "Money & Receipts" },
];

export default function SetupWizard({ onDone }) {
  const { business, loadBusiness } = useAuth();
  const fashion = !!business?.is_fashion;
  const types = fashion ? FASHION_TYPES : TYPES;
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    name: business?.name || "", legal_name: business?.legal_name || "",
    business_type: types.includes(business?.business_type) ? business.business_type : types[0], logo_url: business?.logo_url || "",
    phone: business?.phone || "", email: business?.email || "",
    address: business?.address || "", parish: business?.parish || "Kingston",
    trn: business?.trn || "", registration_info: business?.registration_info || "",
    opening_hours: business?.opening_hours || "",
    currency: business?.currency || "JMD $", tax_label: business?.tax_label || "GCT",
    tax_rate: business?.tax_rate ?? 15, tax_enabled: business?.tax_enabled ?? true,
    payment_methods: (business?.payment_methods || ["Cash", "WiPay/Card", "Bank Transfer"]).join(", "),
    bank_info: business?.bank_info || { bank_name: "", account_name: "", account_number: "", branch: "" },
    receipt_info: business?.receipt_info || { footer: "Thank you for your business!", return_policy: "" },
  });

  const set = (k, v) => setF({ ...f, [k]: v });
  const setBank = (k, v) => setF({ ...f, bank_info: { ...f.bank_info, [k]: v } });
  const setRec = (k, v) => setF({ ...f, receipt_info: { ...f.receipt_info, [k]: v } });

  // Interrupted setup resumes with what was typed: each Continue saves that step's fields onto the same business record (setup stays pending).
  const DRAFT_FIELDS = [["name", "legal_name", "business_type", "logo_url", "phone", "email"], ["address", "parish", "trn", "registration_info", "opening_hours"]];
  const next = async () => {
    const keys = DRAFT_FIELDS[step] || [];
    try { await api.put("/business", Object.fromEntries(keys.filter((k) => f[k] !== undefined && f[k] !== null && f[k] !== "").map((k) => [k, f[k]]))); } catch { /* draft only; Finish reports errors */ }
    setStep(step + 1);
  };

  const finish = async () => {
    setSaving(true);
    try {
      await api.put("/business", {
        ...f, tax_rate: parseFloat(f.tax_rate || 0),
        payment_methods: f.payment_methods.split(",").map((s) => s.trim()).filter(Boolean),
        onboarded: true,
      });
      await loadBusiness();
      toast.success("Your business is all set up!");
      onDone?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/95 backdrop-blur-sm overflow-y-auto" data-testid="setup-wizard">
      <div className="min-h-full flex items-center justify-center p-4">
        <div className="w-full max-w-2xl bg-card border border-border rounded-3xl shadow-2xl overflow-hidden">
          <div className="bg-emerald-600 p-6 text-white">
            <div className="flex items-center gap-2 text-emerald-100 text-xs font-semibold tracking-wider mb-2"><Zap className="w-3.5 h-3.5" /> WELCOME TO NEEC POS</div>
            <h1 className="font-head text-2xl font-extrabold">Let's set up your business</h1>
            <p className="text-emerald-100 text-sm mt-1">{fashion ? "This personalizes your store: dashboard, receipts, product labels and statements." : "This personalizes your whole platform: dashboard, receipts, invoices and statements."}</p>
            <div className="flex gap-2 mt-5">
              {STEPS.map((s, i) => (
                <div key={i} className="flex-1">
                  <div className={`h-1.5 rounded-full ${i <= step ? "bg-white" : "bg-white/30"}`} />
                  <p className={`text-[11px] mt-1.5 flex items-center gap-1 ${i <= step ? "text-white" : "text-emerald-200/60"}`}>
                    {i < step ? <Check className="w-3 h-3" /> : <s.icon className="w-3 h-3" />}{s.title}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="p-6 space-y-4 max-h-[55vh] overflow-y-auto">
            {step === 0 && (
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2 flex items-center gap-4">
                  {f.logo_url ? <img src={f.logo_url} alt="logo" className="w-16 h-16 rounded-2xl object-cover" />
                    : <div className="w-16 h-16 rounded-2xl bg-emerald-500 flex items-center justify-center text-white font-head font-extrabold text-2xl">{(f.name || "N")[0]}</div>}
                  <div className="flex-1"><Label className="text-xs">Business Logo URL</Label>
                    <Input value={f.logo_url} onChange={(e) => set("logo_url", e.target.value)} placeholder="https://…" data-testid="wiz-logo" /></div>
                </div>
                <div><Label className="text-xs">Trading Name *</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder={fashion ? "Bella's Boutique" : "Brown's Hardware"} data-testid="wiz-name" /></div>
                <div><Label className="text-xs">Legal / Registered Name</Label><Input value={f.legal_name} onChange={(e) => set("legal_name", e.target.value)} data-testid="wiz-legal" /></div>
                <div><Label className="text-xs">Business Type</Label>
                  <select value={f.business_type} onChange={(e) => set("business_type", e.target.value)} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="wiz-type">
                    {types.map((t) => <option key={t}>{t}</option>)}
                  </select></div>
                <div><Label className="text-xs">Phone</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="wiz-phone" /></div>
                <div className="sm:col-span-2"><Label className="text-xs">Email</Label><Input value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="wiz-email" /></div>
              </div>
            )}
            {step === 1 && (
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2"><Label className="text-xs">Street Address</Label><Input value={f.address} onChange={(e) => set("address", e.target.value)} data-testid="wiz-address" /></div>
                <div><Label className="text-xs">Parish</Label>
                  <select value={f.parish} onChange={(e) => set("parish", e.target.value)} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="wiz-parish">
                    {PARISHES.map((p) => <option key={p}>{p}</option>)}
                  </select></div>
                <div><Label className="text-xs">TRN</Label><Input value={f.trn} onChange={(e) => set("trn", e.target.value)} data-testid="wiz-trn" /></div>
                <div className="sm:col-span-2"><Label className="text-xs">Business Registration Info</Label><Input value={f.registration_info} onChange={(e) => set("registration_info", e.target.value)} placeholder="Reg. No. / Companies Office" data-testid="wiz-reg" /></div>
                <div className="sm:col-span-2"><Label className="text-xs">Opening Hours (optional)</Label><Input value={f.opening_hours} onChange={(e) => set("opening_hours", e.target.value)} placeholder="Mon–Fri 8am–5pm · Sat 8am–1pm" data-testid="wiz-hours" /></div>
              </div>
            )}
            {step === 2 && (
              <div className="space-y-4">
                <div className="grid sm:grid-cols-3 gap-3">
                  <div><Label className="text-xs">Currency</Label><Input value={f.currency} onChange={(e) => set("currency", e.target.value)} data-testid="wiz-currency" /></div>
                  <div><Label className="text-xs">Tax Label</Label><Input value={f.tax_label} onChange={(e) => set("tax_label", e.target.value)} placeholder="GCT" data-testid="wiz-taxlabel" /></div>
                  <div><Label className="text-xs">Tax Rate (%)</Label><Input type="number" value={f.tax_rate} onChange={(e) => set("tax_rate", e.target.value)} data-testid="wiz-taxrate" /></div>
                </div>
                <div className="flex items-center justify-between rounded-xl border border-border p-3">
                  <div><p className="font-medium text-sm">Charge {f.tax_label || "Tax"} on sales</p></div>
                  <Switch checked={f.tax_enabled} onCheckedChange={(v) => set("tax_enabled", v)} data-testid="wiz-taxenabled" />
                </div>
                <div><Label className="text-xs">Payment Methods (comma separated)</Label><Input value={f.payment_methods} onChange={(e) => set("payment_methods", e.target.value)} data-testid="wiz-methods" /></div>
                <div className="rounded-xl border border-border p-3 space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Bank Transfer Details (shown on invoices)</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <Input value={f.bank_info.bank_name} onChange={(e) => setBank("bank_name", e.target.value)} placeholder="Bank name" data-testid="wiz-bankname" />
                    <Input value={f.bank_info.account_name} onChange={(e) => setBank("account_name", e.target.value)} placeholder="Account name" />
                    <Input value={f.bank_info.account_number} onChange={(e) => setBank("account_number", e.target.value)} placeholder="Account number" />
                    <Input value={f.bank_info.branch} onChange={(e) => setBank("branch", e.target.value)} placeholder="Branch" />
                  </div>
                </div>
                <div><Label className="text-xs">Receipt Footer Message</Label><Textarea value={f.receipt_info.footer} onChange={(e) => setRec("footer", e.target.value)} data-testid="wiz-footer" /></div>
              </div>
            )}
          </div>

          <div className="p-6 border-t border-border flex justify-between">
            <Button variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="wiz-back"><ArrowLeft className="w-4 h-4 mr-1.5" /> Back</Button>
            {step < 2
              ? <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={step === 0 && !f.name} onClick={next} data-testid="wiz-next">Continue <ArrowRight className="w-4 h-4 ml-1.5" /></Button>
              : <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={saving} onClick={finish} data-testid="wiz-finish"><Check className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Finish Setup"}</Button>}
          </div>
        </div>
      </div>
    </div>
  );
}
